import { useCallback, useEffect, useRef } from 'react'
import {
  useApolloClient,
  useMutation,
  useReactiveVar,
  type ApolloClient,
  type ApolloQueryResult
} from '@apollo/client'
import { GetGuestSessionQuery } from '__generated__/graphql'
import { GET_GUEST_SESSION } from 'api/guest-session/get-guest-session'
import { MERGE_GUEST_SESSION } from 'api/guest-session/merge-guest-session'
import { GET_DEMO_BULLSEYE_QUOTA } from 'api/mode-bulls-eye/get-demo-bullseye-quota'
import { GET_DEMO_UP_DOWN_AI_QUOTA } from 'api/mode-up-down-ai-demo/get-demo-up-down-ai-quota'
import { GET_DEMO_UP_DOWN_QUOTA } from 'api/mode-up-down-demo/get-demo-up-down-quota'
import { isGuestClaimDialogOpenVar } from 'shared/store/dialogs'
import { userVar } from 'shared/store/user'
import {
  EMPTY_GUEST_DEMO_ROUNDS,
  guestDemoRoundsVar,
  guestMergeCheckedForVar,
  type GuestDemoRounds
} from '../store'
import { DEMO_QUOTA_QUERIES } from '../utils/demo-quota-queries'
import { countRounds } from '../utils/describe-claim'

type Client = ApolloClient<object>
type GuestSession = GetGuestSessionQuery['getGuestSession']

// NOTE: one-off reads, not watched queries: a watched one would refetch
// with the demo screens' quotas after every merge
const READ = { fetchPolicy: 'no-cache', errorPolicy: 'all' } as const

const quietly = <T>(request: Promise<ApolloQueryResult<T>>) =>
  request.then(
    ({ data }) => data,
    () => undefined
  )

const readSession = (client: Client) =>
  quietly(client.query({ query: GET_GUEST_SESSION, ...READ })).then(
    data => data?.getGuestSession
  )

const readRounds = async (client: Client): Promise<GuestDemoRounds> => {
  const [upDownAi, bullseye, upDown] = await Promise.all([
    quietly(client.query({ query: GET_DEMO_UP_DOWN_AI_QUOTA, ...READ })),
    quietly(client.query({ query: GET_DEMO_BULLSEYE_QUOTA, ...READ })),
    quietly(client.query({ query: GET_DEMO_UP_DOWN_QUOTA, ...READ }))
  ])

  return {
    upDownAi: upDownAi?.getDemoUpDownAiQuota?.roundsPlayed ?? 0,
    bullseye: bullseye?.getDemoBullseyeQuota?.roundsPlayed ?? 0,
    upDown: upDown?.getDemoUpDownQuota?.roundsPlayed ?? 0
  }
}

/**
 * A guest session to claim: bound to no user and unclaimed. The user's own
 * session — provisioned by their first demo operation, with `userId` and
 * without `claimedAt` — or one claimed before is not.
 */
const isClaimable = (session: GuestSession | undefined) =>
  Boolean(session?.id && !session.userId && !session.claimedAt)

/** Demo rounds of a session to claim; `null` when there is none. */
const readClaimableRounds = async (client: Client) => {
  const session = await readSession(client)

  return isClaimable(session) ? readRounds(client) : null
}

/**
 * Offers the demo-results claim once per signed-in user, whatever the way
 * in (wallet, Privy email / OAuth / wallet): the guest session lives in a
 * cookie on the API host, so it survives the OAuth redirect and a reload,
 * and the check simply re-runs when `userVar` is set again (FE-310).
 *
 * The demo quotas are read only for a session to claim: they are guarded
 * operations and would provision a session for a user who has none.
 *
 * A guest session without demo rounds is merged silently (FE-321): the
 * backend hands the guest's attribution and cookie consent over to the user
 * only on `mergeGuestSession`. NOTE: it uses up the user's one claim
 * (`GuestSession.userId` is unique), so demo rounds played later as a guest
 * elsewhere can no longer be claimed; it goes once the backend links the
 * guest's attribution and consent at sign-in.
 *
 * Marks the check done for the user (`useIsGuestMergeSettled`).
 */
export const useMergeGuestSession = () => {
  const client = useApolloClient()
  const user = useReactiveVar(userVar)
  const promptedForUserRef = useRef<string | null>(null)
  const [mergeGuestSession] = useMutation(MERGE_GUEST_SESSION)

  const mergeSilently = useCallback(async () => {
    try {
      await mergeGuestSession({ refetchQueries: DEMO_QUOTA_QUERIES })
    } catch (error) {
      console.warn('Silent mergeGuestSession failed', error)
    }
  }, [mergeGuestSession])

  /** The claim dialog for demo rounds, else a silent merge. */
  const claimOrMerge = useCallback(
    async (rounds: GuestDemoRounds) => {
      if (countRounds(rounds) > 0) {
        guestDemoRoundsVar(rounds)
        isGuestClaimDialogOpenVar(true)
      } else await mergeSilently()
    },
    [mergeSilently]
  )

  const promptIfNeeded = useCallback(async () => {
    if (!user?.id || promptedForUserRef.current === user.id) return
    const userId = user.id
    promptedForUserRef.current = userId
    // NOTE: the user may sign out (or switch) while the check is in flight
    const isCurrent = () => promptedForUserRef.current === userId

    const rounds = await readClaimableRounds(client)
    if (rounds && isCurrent()) await claimOrMerge(rounds)
    if (isCurrent()) guestMergeCheckedForVar(userId)
  }, [claimOrMerge, client, user?.id])

  useEffect(() => {
    if (!user?.id) {
      promptedForUserRef.current = null
      guestDemoRoundsVar(EMPTY_GUEST_DEMO_ROUNDS)
      isGuestClaimDialogOpenVar(false)
      guestMergeCheckedForVar(null)
      return
    }

    void promptIfNeeded()
  }, [promptIfNeeded, user?.id])
}
